import React, { useState, useEffect } from 'react';
import gun from '../../config/gun.config';
import { useUsers } from '../../hooks/useUsers';
import { usePins } from '../../hooks/usePins';
import { useEvents } from '../../hooks/useEvents';
import { IconArrowLeft } from '../ui/Icons';

// Your nullifier hash — only this user sees the dashboard
export const ADMIN_HASH = '0x0fc44868b484ec77fea9e67f1b26c96a3d7c1b68963b3f25adb192d73e12ca51';

interface DailyStats {
  date: string;
  visits: number;
  pin_created: number;
  event_created: number;
  event_rsvp: number;
  message_sent: number;
  call_started: number;
}

interface RecentAction {
  id: string;
  userId: string;
  action: string;
  ts: number;
}

interface Props {
  onClose: () => void;
}

export function AdminDashboard({ onClose }: Props) {
  const allUsers = useUsers();
  const { pins } = usePins();
  const { events, pastEvents } = useEvents();
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [recentActions, setRecentActions] = useState<RecentAction[]>([]);
  const [totalVisits, setTotalVisits] = useState(0);

  // Load analytics from GUN
  useEffect(() => {
    // Load daily stats for last 14 days
    const days: DailyStats[] = [];
    const today = new Date();
    let loaded = 0;

    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];

      gun.get('joystance_analytics').get('daily').get(dateStr).once((data: any) => { // eslint-disable-line no-loop-func
        if (data) {
          days.push({
            date: dateStr,
            visits: Number(data.visits) || 0,
            pin_created: Number(data.pin_created) || 0,
            event_created: Number(data.event_created) || 0,
            event_rsvp: Number(data.event_rsvp) || 0,
            message_sent: Number(data.message_sent) || 0,
            call_started: Number(data.call_started) || 0,
          });
        }
        loaded++;
        if (loaded === 14) {
          setDailyStats(days.sort((a, b) => b.date.localeCompare(a.date)));
          setTotalVisits(days.reduce((sum, d) => sum + d.visits, 0));
        }
      });
    }

    // Load recent actions
    const actions: RecentAction[] = [];
    gun.get('joystance_analytics').get('events').map().once((data: any, id: string) => {
      if (!data || !data.action) return;
      actions.push({ id, userId: data.userId || '', action: data.action, ts: data.ts || 0 });
      setRecentActions([...actions].sort((a, b) => b.ts - a.ts).slice(0, 50));
    });
  }, []);

  const onlineCount = allUsers.filter(u => u.isOnline).length;
  const totalUsers = allUsers.length;

  const todayStr = new Date().toISOString().split('T')[0];
  const todayStats = dailyStats.find(d => d.date === todayStr);

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="text-stabeon-muted hover:text-stabeon-text transition-colors">
          <IconArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-stabeon-text font-bold text-lg">Admin Dashboard</h2>
          <p className="text-stabeon-muted text-xs">Only visible to you</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-2">
        <KPICard label="Online now" value={onlineCount + 1} accent />
        <KPICard label="Total users" value={totalUsers} />
        <KPICard label="Visits (14d)" value={totalVisits} />
        <KPICard label="Active pins" value={pins.length} />
        <KPICard label="Active events" value={events.length} />
        <KPICard label="Past events" value={pastEvents.length} />
      </div>

      {/* Today */}
      {todayStats && (
        <div>
          <p className="section-label">Today</p>
          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Visits" value={todayStats.visits} />
            <MiniStat label="Pins" value={todayStats.pin_created} />
            <MiniStat label="Events" value={todayStats.event_created} />
            <MiniStat label="RSVPs" value={todayStats.event_rsvp} />
            <MiniStat label="Messages" value={todayStats.message_sent} />
            <MiniStat label="Calls" value={todayStats.call_started} />
          </div>
        </div>
      )}

      {/* Daily trend */}
      <div>
        <p className="section-label">Last 14 days</p>
        <div className="flex flex-col gap-1">
          {dailyStats.map(d => (
            <div key={d.date} className="flex items-center gap-2 text-xs">
              <span className="text-stabeon-muted w-20 flex-shrink-0">{d.date.slice(5)}</span>
              <div className="flex-1 h-4 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, (d.visits / Math.max(1, totalVisits / 14)) * 100)}%`,
                    background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                  }}
                />
              </div>
              <span className="text-stabeon-text w-8 text-right">{d.visits}</span>
            </div>
          ))}
          {dailyStats.length === 0 && (
            <p className="text-stabeon-muted text-xs text-center py-4">No data yet — analytics start tracking after this deploy.</p>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <p className="section-label">Recent activity ({recentActions.length})</p>
        <div className="flex flex-col gap-1 max-h-[200px] overflow-y-auto">
          {recentActions.slice(0, 20).map(a => (
            <div key={a.id} className="flex items-center gap-2 text-xs py-1">
              <span className="text-stabeon-accent font-medium w-24 flex-shrink-0 truncate">{a.action}</span>
              <span className="text-stabeon-muted truncate flex-1">{a.userId.slice(-8)}</span>
              <span className="text-stabeon-muted flex-shrink-0">{new Date(a.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
          {recentActions.length === 0 && (
            <p className="text-stabeon-muted text-xs text-center py-2">No tracked actions yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function KPICard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="card px-3 py-3 text-center">
      <p className={`text-xl font-bold ${accent ? 'text-stabeon-accent' : 'text-stabeon-text'}`}>{value}</p>
      <p className="text-stabeon-muted text-[10px] mt-0.5">{label}</p>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 card">
      <span className="text-stabeon-muted text-[10px]">{label}</span>
      <span className="text-stabeon-text text-sm font-semibold">{value}</span>
    </div>
  );
}
